const DEFAULT_ROUTE_COLOR = "0078D4";

export function buildRouteWaypoints(route) {
  const source = route && typeof route === "object" ? route : {};
  const origin = getCoordinates(source.from_location || source.origin?.position || source.origin);
  const orderedStops = getOrderedRouteStops(source);
  const stopPoints = orderedStops
    .map(function (stop) {
      return getCoordinates(
        stop?.access_location || stop?.address_normalized || stop?.address || stop?.position
      );
    })
    .filter(Boolean);

  if (!origin || stopPoints.length === 0) {
    return [];
  }

  const destination = getCoordinates(source.to_location) || origin;
  return [origin].concat(stopPoints, [destination]).filter(function (position, index, list) {
    if (!Array.isArray(position) || position.length < 2) {
      return false;
    }
    if (index === 0) {
      return true;
    }

    const previous = list[index - 1];
    return !previous || previous[0] !== position[0] || previous[1] !== position[1];
  });
}

export function getOrderedRouteStops(route) {
  return (Array.isArray(route?.routes) ? route.routes : [])
    .slice()
    .sort(function (left, right) {
      return Number(left?.order || 0) - Number(right?.order || 0);
    });
}

export function getCoordinates(source) {
  if (!source) {
    return null;
  }

  if (Array.isArray(source) && source.length >= 2) {
    const lng = Number(source[0]);
    const lat = Number(source[1]);
    return Number.isFinite(lng) && Number.isFinite(lat) ? [lng, lat] : null;
  }

  if (Array.isArray(source.coordinates) && source.coordinates.length >= 2) {
    const lng = Number(source.coordinates[0]);
    const lat = Number(source.coordinates[1]);
    if (Number.isFinite(lng) && Number.isFinite(lat)) {
      return [lng, lat];
    }
  }

  if (Array.isArray(source.coordenadas) && source.coordenadas.length >= 2) {
    const lng = Number(source.coordenadas[0]);
    const lat = Number(source.coordenadas[1]);
    if (Number.isFinite(lng) && Number.isFinite(lat)) {
      return [lng, lat];
    }
  }

  const coordinatesObject = source.coordenates || source.coordinates || source.coordenadas || source;
  const lng = Number(
    coordinatesObject?.lng ??
    coordinatesObject?.lon ??
    coordinatesObject?.longitude
  );
  const lat = Number(
    coordinatesObject?.lat ??
    coordinatesObject?.latitude
  );

  if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
    return null;
  }

  return [lng, lat];
}

export function getStoredRouteGeometry(route) {
  return decodeRouteGeometry(route?.geometry);
}

export function getStoredReturnGeometry(route) {
  return decodeRouteGeometry(route?.return_geometry);
}

function decodeRouteGeometry(value) {
  const encoded = typeof value === "string" ? value : "";
  const points = [];
  let index = 0;
  let lat = 0;
  let lng = 0;
  while (index < encoded.length) {
    const deltas = [];
    for (let axis = 0; axis < 2; axis += 1) {
      let result = 0;
      let shift = 0;
      let byte;
      do {
        byte = encoded.charCodeAt(index) - 63;
        index += 1;
        result |= (byte & 0x1f) << shift;
        shift += 5;
      } while (byte >= 0x20 && index < encoded.length);
      deltas.push(result & 1 ? ~(result >> 1) : result >> 1);
    }
    lat += deltas[0];
    lng += deltas[1];
    points.push([lng / 1e5, lat / 1e5]);
  }
  return points.length > 1 ? points : null;
}

export function sanitizeHexColor(value) {
  const current = String(value || "").trim().replace("#", "").toUpperCase();
  const normalized = current.length === 8 ? current.slice(2) : current;
  return /^[0-9A-F]{6}$/.test(normalized) ? normalized : DEFAULT_ROUTE_COLOR;
}

export function lightenHexColor(value, amount) {
  const normalized = sanitizeHexColor(value);
  const factor = Math.min(Math.max(Number(amount) || 0, 0), 1);
  const channels = [0, 2, 4].map(function (index) {
    return parseInt(normalized.slice(index, index + 2), 16);
  });

  return channels
    .map(function (channel) {
      return Math.round(channel + (255 - channel) * factor)
        .toString(16)
        .padStart(2, "0");
    })
    .join("")
    .toUpperCase();
}

export function buildArrowDataUri(color) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="12" viewBox="0 0 16 12"><polyline points="3,1 13,6 3,11" fill="none" stroke="${color}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  return "data:image/svg+xml;base64," + btoa(svg);
}

export function stableStringify(value) {
  return JSON.stringify(sortKeys(value));
}

function sortKeys(value) {
  if (Array.isArray(value)) {
    return value.map(sortKeys);
  }

  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce(function (accumulator, key) {
        accumulator[key] = sortKeys(value[key]);
        return accumulator;
      }, {});
  }

  return value;
}
